import React, { useState } from 'react';
import clsx from 'clsx';
import { Light as SyntaxHighlighter } from 'react-syntax-highlighter';
import { tomorrow } from 'react-syntax-highlighter/dist/esm/styles/hljs';

/** Log entry interface, including optional token/pricing fields. */
export interface ILLMLogEntry {
    p_id: string;
    system_instructions?: any;
    user_text?: any;
    assistant_answer?: any;
    llm_response?: any;
    sanitized_output?: any;
    model?: string;
    elapsed_time?: number;

    /* Token usage fields */
    prompt_tokens?: number;
    completion_tokens?: number;
    total_tokens?: number;

    /* Pricing fields */
    price_prompt_tokens?: number;       // cost of prompt tokens
    price_completion_tokens?: number;   // cost of completion tokens
    price_total_tokens?: number;        // total cost
}

/** Props for the main LLMLog component. */
interface ILLMLogProps {
    title: string;
    description?: string;
    className?: string;
    llmLogs?: ILLMLogEntry[];
}

/**
 * Helper function to render a value with syntax highlighting.
 * If the value is an object, it will be stringified and rendered inside
 * a SyntaxHighlighter component.
 */
const renderValue = (value: any) => {
    if (typeof value === 'object') {
        return (
            <SyntaxHighlighter language="json" style={tomorrow}>
                {JSON.stringify(value, null, 2)}
            </SyntaxHighlighter>
        );
    }
    return value;
};

/**
 * A collapsible section component for wrapping each field.
 */
const CollapsibleSection: React.FC<{ title: string; children: React.ReactNode }> = ({
                                                                                        title,
                                                                                        children,
                                                                                    }) => {
    const [collapsed, setCollapsed] = useState(true);
    return (
        <div className="mb-2">
            <div
                className="flex justify-between items-center cursor-pointer"
                onClick={() => setCollapsed(!collapsed)}
            >
                <strong>{title}</strong>
                <button className="text-sm text-blue-500">
                    {collapsed ? 'Expand' : 'Collapse'}
                </button>
            </div>
            {!collapsed && <div className="mt-1 pl-4">{children}</div>}
        </div>
    );
};

/**
 * Component for a single LLM log entry.
 * Fields are shown in collapsible sections.
 * Token usage includes cost in parentheses after the token count.
 */
const LLMLogEntryComponent: React.FC<{ log: ILLMLogEntry }> = ({ log }) => {
    return (
        <div className="mb-4 p-3 border rounded-lg bg-base-100">
            <h4 className="text-base font-semibold mb-2">LLM Interaction ({log.p_id})</h4>

            {/* Collapsible sections for different fields */}
            <CollapsibleSection title="System instruction">
                <div className="whitespace-pre-wrap">{renderValue(log.system_instructions)}</div>
            </CollapsibleSection>

            <CollapsibleSection title="User text">
                <div className="whitespace-pre-wrap">{renderValue(log.user_text)}</div>
            </CollapsibleSection>

            <CollapsibleSection title="Assistant answer">
                <div className="whitespace-pre-wrap">{renderValue(log.assistant_answer)}</div>
            </CollapsibleSection>

            <CollapsibleSection title="Sanitized Output">
                <div className="whitespace-pre-wrap">{renderValue(log.sanitized_output)}</div>
            </CollapsibleSection>

            <CollapsibleSection title="Raw (Non-Sanitized) Output">
                <div className="whitespace-pre-wrap">{renderValue(log.llm_response)}</div>
            </CollapsibleSection>

            {log.model && (
                <p className="text-sm mb-2">
                    <strong>Model:</strong> {log.model}
                </p>
            )}

            {typeof log.elapsed_time !== 'undefined' && (
                <p className="text-sm mb-2">
                    <strong>Elapsed Time (s):</strong> {log.elapsed_time}
                </p>
            )}

            {/* Token usage: show cost in parentheses */}
            {typeof log.prompt_tokens !== 'undefined' && (
                <p className="text-sm mb-2">
                    <strong>Prompt Tokens:</strong> {log.prompt_tokens}
                    {typeof log.price_prompt_tokens !== 'undefined' && (
                        <> (${log.price_prompt_tokens})</>
                    )}
                </p>
            )}
            {typeof log.completion_tokens !== 'undefined' && (
                <p className="text-sm mb-2">
                    <strong>Completion Tokens:</strong> {log.completion_tokens}
                    {typeof log.price_completion_tokens !== 'undefined' && (
                        <> (${log.price_completion_tokens})</>
                    )}
                </p>
            )}
            {typeof log.total_tokens !== 'undefined' && (
                <p className="text-sm mb-2">
                    <strong>Total Tokens:</strong> {log.total_tokens}
                    {typeof log.price_total_tokens !== 'undefined' && (
                        <> (${log.price_total_tokens})</>
                    )}
                </p>
            )}
        </div>
    );
};

/**
 * Main LLMLog component that renders a collapsible section for all logs.
 * Only renders in "development" or "testing" environment.
 */
const LLMLog: React.FC<ILLMLogProps> = ({
                                            title,
                                            description = '',
                                            className,
                                            llmLogs = [],
                                        }) => {
    // Only render logs if environment is dev or testing
    const env = import.meta.env.VITE_APP_ENV;
    if (env !== 'development' && env !== 'testing') {
        return null;
    }

    const [isSectionCollapsed, setIsSectionCollapsed] = useState(false);

    return (
        <div className={clsx('card bg-gray-100', className)}>
            {/* Card Header with collapse toggle */}
            <div
                className="card-header flex justify-between items-center cursor-pointer"
                onClick={() => setIsSectionCollapsed(!isSectionCollapsed)}
            >
                <h3 className="card-title">{title}</h3>
                <button className="text-sm text-blue-500">
                    {isSectionCollapsed ? 'Expand' : 'Collapse'}
                </button>
            </div>

            {/* Card Body */}
            {!isSectionCollapsed && (
                <div className="card-body">
                    {description && (
                        <div className="mb-4">
                            <p className="whitespace-pre-wrap text-sm">{description}</p>
                        </div>
                    )}
                    {llmLogs.length > 0 ? (
                        <div>
                            {llmLogs.map((log) => (
                                <LLMLogEntryComponent key={log.p_id} log={log} />
                            ))}
                        </div>
                    ) : (
                        <p className="text-sm">No LLM logs available.</p>
                    )}
                </div>
            )}
        </div>
    );
};

export { LLMLog, type ILLMLogProps };
